import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { FiEdit, FiMail, FiPhone, FiClock, FiBriefcase, FiTarget, FiMapPin, FiUser, FiChevronLeft, FiChevronRight, FiFlag } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import theme from '../../styles/theme';

const CardWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[3]};
`;

const CheckboxWrapper = styled.label`
  display: flex;
  align-items: center;
  padding-top: ${theme.spacing[6]};
  cursor: pointer;
  flex-shrink: 0;

  input {
    width: 20px;
    height: 20px;
    accent-color: ${theme.colors.primary[500]};
    cursor: pointer;
  }
`;

const CardContainer = styled.div`
  flex: 1;
  min-width: 0;
  background: ${theme.colors.background.secondary};
  border: 1px solid ${(props) => (props.$selected ? theme.colors.primary[500] : theme.colors.border.primary)};
  border-radius: ${theme.borderRadius.xl};
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  ${(props) => props.$selected && `box-shadow: 0 0 0 2px ${theme.colors.primary[500]}40;`}

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    border-color: ${theme.colors.primary[300]};
  }
`;

const Header = styled.div`
  background: linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.primary[600]} 100%);
  padding: ${theme.spacing[6]};
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${theme.colors.text.primary};
`;

const LeadAvatar = styled.div`
  width: 60px;
  height: 60px;
  border-radius: ${theme.borderRadius.full};
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${theme.fontSizes['2xl']};
  font-weight: 700;
  text-transform: uppercase;
  border: 3px solid rgba(255, 255, 255, 0.3);
`;

const LeadInfo = styled.div`
  flex: 1;
  margin-left: ${theme.spacing[4]};
`;

const LeadName = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.xl};
  font-weight: 700;
  margin: 0 0 ${theme.spacing[1]} 0;
`;

const LeadContact = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: ${theme.fontSizes.sm};
  margin: 0;
`;

const ContentSection = styled.div`
  padding: ${theme.spacing[4]};
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[4]};
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
`;

const DetailIcon = styled.div`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.base};
  min-width: 20px;
`;

const ContatosSection = styled.div`
  margin: ${theme.spacing[4]} 0;
  padding: ${theme.spacing[3]};
  background: ${theme.colors.background.primary};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border.primary};
`;

const ContatosTitle = styled.h4`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  margin: 0 0 ${theme.spacing[3]} 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const ContatosViewport = styled.div`
  position: relative;
  overflow: visible;
  padding: 8px 40px;
`;

const ContatosCarousel = styled.div`
  overflow: hidden;
`;

const ContatosTrack = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
  transition: transform 0.3s ease;
  transform: translateX(-${props => props.$offset}px);
`;

const ArrowButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid ${theme.colors.primary[500]};
  background: ${theme.colors.background.secondary};
  color: ${theme.colors.primary[500]};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 20;
  font-size: 18px;
  font-weight: bold;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;
  backdrop-filter: blur(4px);
  padding: 0;
  left: ${props => props.$left !== undefined ? `${props.$left}px` : 'auto'};
  right: ${props => props.$right !== undefined ? `${props.$right}px` : 'auto'};
  
  &:hover:not(:disabled) {
    background: ${theme.colors.primary[500]};
    color: ${theme.colors.text.primary};
    transform: translateY(-50%) scale(1.1);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }
  
  &:active:not(:disabled) {
    transform: translateY(-50%) scale(0.95);
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
    border-color: ${theme.colors.border.primary};
  }
`;

const ContatoCard = styled.div`
  flex: 0 0 auto;
  min-width: 120px;
  max-width: 140px;
  padding: ${theme.spacing[3]};
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border.primary};
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${theme.spacing[2]};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${theme.colors.primary[400]};
    background: ${theme.colors.background.tertiary};
  }
`;

const ContatoAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: ${theme.borderRadius.full};
  background: linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.primary[600]} 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${theme.fontSizes.xs};
  font-weight: 700;
  color: ${theme.colors.text.primary};
  text-transform: uppercase;
`;

const ContatoNome = styled.span`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.xs};
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

const ContatoTelefone = styled.span`
  color: ${theme.colors.text.secondary};
  font-size: 10px;
  display: flex;
  align-items: center;
  gap: 2px;
`;

const EtapaBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  background: ${(props) => props.$bg || `${theme.colors.primary[500]}20`};
  color: ${(props) => props.$color || theme.colors.primary[600]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.fontSizes.xs};
  font-weight: 500;
`;

const EtapaDestaque = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[4]};
  background: ${(props) => props.$bg || `${theme.colors.primary[500]}18`};
  border: 1px solid ${(props) => props.$border || theme.colors.primary[400]};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  color: ${(props) => props.$color || theme.colors.primary[600]};
  box-shadow: 0 2px 8px ${(props) => (props.$shadow ? `${props.$shadow}25` : `${theme.colors.primary[500]}15`)};
`;

const SegmentoBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  background: ${theme.colors.success[500]}20;
  color: ${theme.colors.success[600]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.fontSizes.xs};
  font-weight: 600;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing[2]};
  margin-top: ${theme.spacing[3]};
  padding-top: ${theme.spacing[3]};
  border-top: 1px solid ${theme.colors.border.primary};
`;

const Tag = styled.span`
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.fontSizes.xs};
  font-weight: 500;
`;

const Actions = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
`;

const ActionButton = styled.button`
  flex: 1;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${props => props.$primary ? theme.colors.primary[500] : theme.colors.border.primary};
  background: ${props => props.$primary ? theme.colors.primary[500] : 'transparent'};
  color: ${props => props.$primary ? theme.colors.text.primary : theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${props => props.$primary ? theme.colors.primary[600] : theme.colors.background.primary};
    color: ${props => props.$primary ? theme.colors.text.primary : theme.colors.primary[500]};
    border-color: ${props => props.$primary ? theme.colors.primary[600] : theme.colors.primary[500]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ActionIcon = styled.div`
  font-size: ${theme.fontSizes.base};
`;

/** Cores por ordem da etapa (1, 2, 3...) para fácil identificação visual */
const ETAPA_CORES = [
  { bg: `${theme.colors.primary[500]}22`, color: theme.colors.primary[400], border: theme.colors.primary[500], shadow: theme.colors.primary[500] },      // 1 - azul
  { bg: `${theme.colors.success[500]}22`, color: theme.colors.success[400], border: theme.colors.success[500], shadow: theme.colors.success[500] },    // 2 - verde
  { bg: `${theme.colors.warning[500]}22`, color: theme.colors.warning[400], border: theme.colors.warning[500], shadow: theme.colors.warning[500] },    // 3 - âmbar
  { bg: '#8b5cf622', color: '#a78bfa', border: '#8b5cf6', shadow: '#8b5cf6' },    // 4 - violeta
  { bg: '#f43f5e22', color: '#fb7185', border: '#f43f5e', shadow: '#f43f5e' },   // 5 - rose
  { bg: '#06b6d422', color: '#22d3ee', border: '#06b6d4', shadow: '#06b6d4' },   // 6 - cyan
  { bg: '#ea580c22', color: '#fb923c', border: '#ea580c', shadow: '#ea580c' },   // 7 - laranja
  { bg: '#c026d322', color: '#e879f9', border: '#c026d3', shadow: '#c026d3' },   // 8 - fuchsia
];

const getEtapaCores = (ordem) => {
  if (ordem == null || ordem === undefined) return ETAPA_CORES[0];
  const index = Math.max(0, Number(ordem) - 1) % ETAPA_CORES.length;
  return ETAPA_CORES[index];
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR');
};

const getInitials = (name) => {
  if (!name) return 'L';
  const words = name.trim().split(' ');
  if (words.length === 1) return words[0][0].toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

const LeadCard = ({ lead, selectable = false, selected = false, onToggle }) => {
  const navigate = useNavigate();
  const [carouselOffset, setCarouselOffset] = useState(0);
  const trackRef = useRef(null);
  const CARD_WIDTH = 152; // min-width + gap
  const compact = selectable; // Na tela Preparar Mensagens: apenas info básica

  // Resetar offset do carrossel quando o lead mudar
  React.useEffect(() => {
    setCarouselOffset(0);
  }, [lead.id]);

  const handleEdit = (e) => {
    if (selectable && e?.target?.closest?.('[data-lead-checkbox]')) return;
    navigate(`/leads/${lead.id}/editar`);
  };

  const handleCardClick = (e) => {
    if (!selectable || !onToggle) return;
    if (e.target.closest('[data-lead-checkbox]') || e.target.closest('button')) return;
    onToggle(lead.id, !selected);
  };

  const displayName = lead.empresa || 'Lead sem empresa';
  const contatos = lead.contatos || [];
  const primeiroContato = contatos.length > 0 ? contatos[0] : null;
  const displayContact = lead.email || (primeiroContato?.telefone) || 'Sem contato';

  // Carrossel - calcula baseado no espaço disponível (apenas quando não compact)
  const VISIBLE_CARDS = 2;
  const maxOffset = Math.max(0, (contatos.length - VISIBLE_CARDS) * CARD_WIDTH);
  const canScrollLeft = carouselOffset > 0;
  const canScrollRight = carouselOffset < maxOffset;
  const showArrows = contatos.length > VISIBLE_CARDS;

  const handleScrollLeft = () => {
    setCarouselOffset(prev => Math.max(0, prev - CARD_WIDTH));
  };

  const handleScrollRight = () => {
    setCarouselOffset(prev => Math.min(maxOffset, prev + CARD_WIDTH));
  };

  const getContatoInitials = (nome) => {
    if (!nome) return '?';
    const words = nome.trim().split(' ');
    if (words.length === 1) return words[0][0].toUpperCase();
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  };

  // Formatar localização
  const getLocalizacao = () => {
    if (lead.cidade && lead.estado) {
      return `${lead.cidade} - ${lead.estado}`;
    }
    if (lead.cidade) return lead.cidade;
    if (lead.estado) return lead.estado;
    return null;
  };

  const cardContent = (
    <CardContainer $selected={selectable && selected}>
      <Header>
        <LeadAvatar>
          {getInitials(lead.empresa)}
        </LeadAvatar>
        <LeadInfo>
          <LeadName>{displayName}</LeadName>
          <LeadContact>{displayContact}</LeadContact>
        </LeadInfo>
      </Header>

      <ContentSection>
        {lead.etapa && (() => {
          const cores = getEtapaCores(lead.etapa.ordem);
          return (
            <EtapaDestaque $bg={cores.bg} $color={cores.color} $border={cores.border} $shadow={cores.shadow}>
              <FiFlag size={18} />
              <span>Etapa {lead.etapa.ordem != null ? lead.etapa.ordem : ''}: {lead.etapa.nome}</span>
            </EtapaDestaque>
          );
        })()}
        <Details>
          {lead.email && (
            <DetailItem>
              <DetailIcon><FiMail /></DetailIcon>
              <span>{lead.email}</span>
            </DetailItem>
          )}
          {lead.empresa && (
            <DetailItem>
              <DetailIcon><FiBriefcase /></DetailIcon>
              <span>{lead.empresa}</span>
            </DetailItem>
          )}
          {getLocalizacao() && (
            <DetailItem>
              <DetailIcon><FiMapPin /></DetailIcon>
              <span>{getLocalizacao()}</span>
            </DetailItem>
          )}
          {lead.origem && (
            <DetailItem>
              <DetailIcon><FiTarget /></DetailIcon>
              <span>{lead.origem.nome}</span>
            </DetailItem>
          )}
          {!compact && (
            <DetailItem>
              <DetailIcon><FiClock /></DetailIcon>
              <span>Cadastrado em {formatDate(lead.created_at)}</span>
            </DetailItem>
          )}
        </Details>

        {!compact && contatos.length > 0 && (
          <ContatosSection>
            <ContatosTitle>
              <FiUser />
              Contatos ({contatos.length})
            </ContatosTitle>
            <ContatosViewport>
              <ContatosCarousel>
                <ContatosTrack ref={trackRef} $offset={carouselOffset}>
                  {contatos.map((contato, index) => (
                    <ContatoCard key={contato.id || `contato-${index}`}>
                      <ContatoAvatar>
                        {getContatoInitials(contato.nome)}
                      </ContatoAvatar>
                      <ContatoNome title={contato.nome || 'Sem nome'}>
                        {contato.nome || 'Sem nome'}
                      </ContatoNome>
                      {contato.telefone && (
                        <ContatoTelefone>
                          <FiPhone size={10} />
                          {contato.telefone}
                        </ContatoTelefone>
                      )}
                    </ContatoCard>
                  ))}
                </ContatosTrack>
              </ContatosCarousel>
              {showArrows && (
                <>
                  <ArrowButton
                    type="button"
                    $left={4}
                    disabled={!canScrollLeft}
                    onClick={handleScrollLeft}
                    title="Anterior"
                  >
                    ‹
                  </ArrowButton>
                  <ArrowButton
                    type="button"
                    $right={4}
                    disabled={!canScrollRight}
                    onClick={handleScrollRight}
                    title="Próximo"
                  >
                    ›
                  </ArrowButton>
                </>
              )}
            </ContatosViewport>
          </ContatosSection>
        )}

        {!compact && (
          <TagsContainer>
            {lead.segmento && (
              <SegmentoBadge>
                {lead.segmento}
              </SegmentoBadge>
            )}
            {lead.etapa && (() => {
              const cores = getEtapaCores(lead.etapa.ordem);
              return (
                <EtapaBadge $bg={cores.bg} $color={cores.color}>
                  {lead.etapa.ordem != null ? `${lead.etapa.ordem}. ` : ''}{lead.etapa.nome}
                </EtapaBadge>
              );
            })()}
            {lead.funil && (
              <Tag>
                {lead.funil.nome}
              </Tag>
            )}
          </TagsContainer>
        )}

        {!compact && (
          <Actions style={{ marginTop: theme.spacing[4] }}>
            <ActionButton $primary onClick={handleEdit}>
              <ActionIcon><FiEdit /></ActionIcon>
              Editar
            </ActionButton>
          </Actions>
        )}
      </ContentSection>
    </CardContainer>
  );

  if (selectable) {
    return (
      <CardWrapper onClick={handleCardClick}>
        <CheckboxWrapper data-lead-checkbox>
          <input
            type="checkbox"
            checked={selected}
            onChange={(e) => onToggle?.(lead.id, e.target.checked)}
            onClick={(e) => e.stopPropagation()}
          />
        </CheckboxWrapper>
        {cardContent}
      </CardWrapper>
    );
  }

  return cardContent;
};

export default LeadCard;
